/**
 * TextStep 组件
 * 用于显示 text 类型的 ProcessStep，支持 markdown 渲染
 */
import React, { useMemo } from 'react';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAtom } from 'jotai';
import { isDarkAtom } from '../../../../../atoms/shared/uiAtoms';
import { ProcessStep } from '../../../../../types';
import { TempoProtocolLink } from '../../../../../components/v3/TempoProtocolLink';
import { TEMPO_PROTOCOL_NAME } from '../../../../../constants/tempoProtocol';

interface TextStepProps {
  step: ProcessStep;
  forceTheme?: 'light' | 'dark';
}

export const TextStep: React.FC<TextStepProps> = ({ step, forceTheme }) => {
  const [isDark] = useAtom(isDarkAtom);

  // 类型守卫：确保是text类型的step
  if (step.type !== 'text') {
    return null;
  }

  // 计算markdown prose样式
  const getProseClasses = useMemo(() => {
    let isDarkMode: boolean;
    if (forceTheme) {
      isDarkMode = forceTheme === 'dark';
    } else {
      isDarkMode = isDark;
    }

    return isDarkMode ? 'prose prose-invert prose-sm max-w-none message-markdown' : 'prose prose-sm max-w-none message-markdown';
  }, [forceTheme, isDark]);

  // 渲染 markdown 内容
  const renderMarkdownContent = (content: string) => {
    return (
      <div className={getProseClasses}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          urlTransform={(url) => {
            if (url.startsWith(TEMPO_PROTOCOL_NAME + '://')) {
              return url;
            }
            return url;
          }}
          components={{
            a: ({ href, children, ...props }) => {
              if (href?.startsWith(TEMPO_PROTOCOL_NAME + '://')) {
                return <TempoProtocolLink href={href}>{children}</TempoProtocolLink>;
              }
              return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  if (!step.text) {
    return null;
  }

  return (
    <div>
      {renderMarkdownContent(step.text)}
    </div>
  );
};

export default TextStep;
